import ClickableTableCell from 'Components/ClickableTableCell'
import CustomTablePagination from 'Components/CustomTablePagination'
import EnhancedTableHead, { type HeadCell } from 'Components/EnhancedTableHead/EnhancedTableHead'
import StyledTableRow from 'Components/StyledTableRow'
import { WithLoader } from 'Components/WithLoader'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import type {
  FetchTicketSaleListParams,
  TicketSale
} from 'Services/SalesManagement/Features/TicketSale/types'
import { datetimeFormatter } from 'Util/formatters'
import { Paper, Table, TableBody, TableCell, TableContainer } from '@mui/material'
import { t } from 'ttag'

interface TicketSaleListProps {
  rows: TicketSale[]
  isLoading: boolean
  params: FetchTicketSaleListParams
  handleRequestSort: (event: React.MouseEvent<unknown, MouseEvent>, property: string) => void
  handleChangePage: (event: unknown, newPage: number) => void
  count: number
  page: number
  onRowClick: (id: string) => void
  selectedTicketSaleId?: string
}

export default function TicketSaleList({
  rows,
  isLoading,
  params,
  handleRequestSort,
  handleChangePage,
  count,
  page,
  onRowClick,
  selectedTicketSaleId
}: Readonly<TicketSaleListProps>) {
  const headCells: HeadCell[] = [
    { id: 'transaction_id', label: t`Transaction ID` },
    { id: 'product_name', label: t`Product` },
    { id: 'target_identifier', label: t`Target identifier` },
    { id: 'authority', label: t`Authority` },
    { id: 'validity_starts', label: t`Validity starts` },
    { id: 'validity_ends', label: t`Validity ends` },
    { id: 'created', label: t`Created`, sortable: true }
  ]

  const { getAuthorityNameByCode } = useAuthorityContext()

  return (
    <WithLoader loading={isLoading}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        <TableContainer>
          <Table size="small" aria-label={t`Ticket sale targets table`}>
            <EnhancedTableHead
              orderBy={params.ordering}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {rows.map(row => (
                <StyledTableRow
                  hover
                  tabIndex={-1}
                  key={row.id}
                  selected={selectedTicketSaleId === row.id}
                >
                  <ClickableTableCell onClick={() => onRowClick(row.id)}>
                    {row.transaction_id}
                  </ClickableTableCell>
                  <TableCell>{row.product_name}</TableCell>
                  <TableCell>{row.target_identifier}</TableCell>
                  <TableCell>{getAuthorityNameByCode(row.authority)}</TableCell>
                  <TableCell>{datetimeFormatter(row.validity_starts)}</TableCell>
                  <TableCell>{datetimeFormatter(row.validity_ends)}</TableCell>
                  <TableCell>{datetimeFormatter(row.created)}</TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomTablePagination count={count} page={page} onPageChange={handleChangePage} />
      </Paper>
    </WithLoader>
  )
}
